export type LogLevel = 'debug' | 'info' | 'warn' | 'error';
export type LogCategory = 'keyboard' | 'sound' | 'game' | 'modal' | 'navigation' | 'storage' | 'effects' | 'shop' | 'general';

export interface LogEntry {
  timestamp: Date;
  level: LogLevel;
  category: LogCategory;
  message: string;
  data?: any;
  caller?: string;
}

export interface LoggingConfig {
  enabled: boolean;
  level: LogLevel;
  categories: LogCategory[];
  maxLogs: number;
  persistLogs: boolean;
  showTimestamp: boolean;
  showCategory: boolean;
  showCaller: boolean;
}

const defaultConfig: LoggingConfig = {
  enabled: true,
  level: 'debug',
  categories: ['keyboard', 'sound', 'game', 'modal', 'navigation', 'storage', 'effects', 'shop', 'general'],
  maxLogs: 1000,
  persistLogs: false,
  showTimestamp: true,
  showCategory: true,
  showCaller: true,
};

class LoggingService {
  private logs: LogEntry[] = [];
  private config: LoggingConfig;
  private listeners: ((log: LogEntry) => void)[] = [];

  constructor() {
    this.config = this.loadConfig();
    this.setupConsoleStyles();
    
    // Expose logging service to window for debugging
    if (typeof window !== 'undefined') {
      (window as any).gameLogging = this;
    }
  }

  private loadConfig(): LoggingConfig {
    if (typeof window === 'undefined') return defaultConfig;
    
    try {
      const saved = localStorage.getItem('loggingConfig');
      if (saved) {
        return { ...defaultConfig, ...JSON.parse(saved) };
      }
    } catch (error) {
      console.error('Failed to load logging config:', error);
    }
    return defaultConfig;
  }

  private saveConfig(): void {
    if (typeof window === 'undefined') return;
    
    try {
      localStorage.setItem('loggingConfig', JSON.stringify(this.config));
    } catch (error) {
      console.error('Failed to save logging config:', error);
    }
  }

  private setupConsoleStyles(): void {
    // Define console styles for different log levels and categories
    this.styles = {
      debug: 'color: #888; font-weight: normal;',
      info: 'color: #2196F3; font-weight: normal;',
      warn: 'color: #FF9800; font-weight: bold;',
      error: 'color: #F44336; font-weight: bold;',
      
      // Category colors
      keyboard: 'background: #9C27B0; color: white; padding: 2px 4px; border-radius: 3px;',
      sound: 'background: #4CAF50; color: white; padding: 2px 4px; border-radius: 3px;',
      game: 'background: #2196F3; color: white; padding: 2px 4px; border-radius: 3px;',
      modal: 'background: #FF5722; color: white; padding: 2px 4px; border-radius: 3px;',
      navigation: 'background: #00BCD4; color: white; padding: 2px 4px; border-radius: 3px;',
      storage: 'background: #795548; color: white; padding: 2px 4px; border-radius: 3px;',
      effects: 'background: #E91E63; color: white; padding: 2px 4px; border-radius: 3px;',
      shop: 'background: #FFC107; color: black; padding: 2px 4px; border-radius: 3px;',
      general: 'background: #607D8B; color: white; padding: 2px 4px; border-radius: 3px;',
    };
  }

  private styles: Record<string, string> = {};

  private shouldLog(level: LogLevel, category: LogCategory): boolean {
    if (!this.config.enabled) return false;
    if (!this.config.categories.includes(category)) return false;
    
    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error'];
    const configLevelIndex = levels.indexOf(this.config.level);
    const messageLevelIndex = levels.indexOf(level);
    
    return messageLevelIndex >= configLevelIndex;
  }

  private formatMessage(entry: LogEntry): string[] {
    let message = '';
    const styles: string[] = [];
    
    if (this.config.showTimestamp) {
      const time = entry.timestamp.toTimeString().split(' ')[0];
      message += `[${time}] `;
    }
    
    if (this.config.showCategory) {
      message += `%c${entry.category.toUpperCase()}%c `;
      styles.push(this.styles[entry.category] || '');
      styles.push(''); // Reset style
    }
    
    message += `%c${entry.message}`;
    styles.push(this.styles[entry.level] || '');
    
    // Add caller info
    if (this.config.showCaller && entry.caller && entry.caller !== 'unknown') {
      message += ` %c(${entry.caller})`;
      styles.push('color: #999; font-style: italic; font-size: 0.9em;');
    }
    
    return [message, ...styles];
  }

  private getCaller(): string {
    try {
      const error = new Error();
      const stack = error.stack?.split('\n') || [];
      
      // Find the first stack frame that's not from the logging service
      for (let i = 3; i < stack.length; i++) {
        const line = stack[i];
        if (!line.includes('/logging.ts') && !line.includes('/LoggingContext.tsx')) {
          // Extract file and line info
          const match = line.match(/\s+at\s+(?:.*?\s+\()?(.*?):(\d+):(\d+)\)?/);
          if (match) {
            const fullPath = match[1];
            // Extract just the filename and line, removing query params
            let fileName = fullPath.split('/').pop() || fullPath;
            fileName = fileName.split('?')[0]; // Remove query params like ?t=timestamp
            return `${fileName}:${match[2]}`;
          }
          
          // Alternative format for some browsers
          const altMatch = line.match(/(?:.*?@)?(.*?):(\d+):(\d+)/);
          if (altMatch) {
            const fullPath = altMatch[1];
            let fileName = fullPath.split('/').pop() || fullPath;
            fileName = fileName.split('?')[0]; // Remove query params like ?t=timestamp
            return `${fileName}:${altMatch[2]}`;
          }
        }
      }
    } catch (e) {
      // Fallback if stack trace parsing fails
    }
    return 'unknown';
  }

  private log(level: LogLevel, category: LogCategory, message: string, data?: any): void {
    if (!this.shouldLog(level, category)) return;
    
    const entry: LogEntry = {
      timestamp: new Date(),
      level,
      category,
      message,
      data,
      caller: this.getCaller(),
    };
    
    // Add to logs array
    this.logs.push(entry);
    if (this.logs.length > this.config.maxLogs) {
      this.logs.shift();
    }
    
    // Console output
    const [formattedMessage, ...styles] = this.formatMessage(entry);
    const consoleMethod = level === 'error' ? 'error' : level === 'warn' ? 'warn' : 'log';
    
    if (data !== undefined) {
      console[consoleMethod](formattedMessage, ...styles, data);
    } else {
      console[consoleMethod](formattedMessage, ...styles);
    }
    
    // Notify listeners
    this.listeners.forEach(listener => listener(entry));
    
    // Persist if enabled
    if (this.config.persistLogs) {
      this.persistLogs();
    }
  }

  // Public logging methods
  debug(category: LogCategory, message: string, data?: any): void {
    this.log('debug', category, message, data);
  }

  info(category: LogCategory, message: string, data?: any): void {
    this.log('info', category, message, data);
  }

  warn(category: LogCategory, message: string, data?: any): void {
    this.log('warn', category, message, data);
  }

  error(category: LogCategory, message: string, data?: any): void {
    this.log('error', category, message, data);
  }

  // Configuration methods
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
    this.saveConfig();
  }

  setLevel(level: LogLevel): void {
    this.config.level = level;
    this.saveConfig();
  }

  setCategories(categories: LogCategory[]): void {
    this.config.categories = categories;
    this.saveConfig();
  }

  enableCategory(category: LogCategory): void {
    if (!this.config.categories.includes(category)) {
      this.config.categories.push(category);
      this.saveConfig();
    }
  }

  disableCategory(category: LogCategory): void {
    this.config.categories = this.config.categories.filter(c => c !== category);
    this.saveConfig();
  }

  setShowCaller(show: boolean): void {
    this.config.showCaller = show;
    this.saveConfig();
  }

  setShowTimestamp(show: boolean): void {
    this.config.showTimestamp = show;
    this.saveConfig();
  }

  setShowCategory(show: boolean): void {
    this.config.showCategory = show;
    this.saveConfig();
  }

  // Log management
  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  clearLogs(): void {
    this.logs = [];
    if (this.config.persistLogs) {
      localStorage.removeItem('gameLogs');
    }
  }

  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  private persistLogs(): void {
    try {
      localStorage.setItem('gameLogs', JSON.stringify(this.logs));
    } catch (error) {
      console.error('Failed to persist logs:', error);
    }
  }

  // Event listeners
  addListener(listener: (log: LogEntry) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  // Utility methods for common logging patterns
  logAction(category: LogCategory, action: string, details?: any): void {
    this.info(category, `Action: ${action}`, details);
  }

  logEvent(category: LogCategory, event: string, details?: any): void {
    this.debug(category, `Event: ${event}`, details);
  }

  logStateChange(category: LogCategory, stateName: string, oldValue: any, newValue: any): void {
    this.info(category, `State change: ${stateName}`, { oldValue, newValue });
  }

  logPerformance(category: LogCategory, operation: string, duration: number): void {
    const level = duration > 100 ? 'warn' : 'debug';
    this.log(level, category, `Performance: ${operation} took ${duration}ms`);
  }
}

// Create singleton instance
export const logger = new LoggingService();

// Console helper commands
if (typeof window !== 'undefined') {
  (window as any).enableLogging = (category?: LogCategory) => {
    if (category) {
      logger.enableCategory(category);
      console.log(`Enabled logging for category: ${category}`);
    } else {
      logger.setEnabled(true);
      console.log('Logging enabled');
    }
  };

  (window as any).disableLogging = (category?: LogCategory) => {
    if (category) {
      logger.disableCategory(category);
      console.log(`Disabled logging for category: ${category}`);
    } else {
      logger.setEnabled(false);
      console.log('Logging disabled');
    }
  };

  (window as any).setLogLevel = (level: LogLevel) => {
    logger.setLevel(level);
    console.log(`Log level set to: ${level}`);
  };

  (window as any).clearLogs = () => {
    logger.clearLogs();
    console.log('Logs cleared');
  };

  (window as any).exportLogs = () => {
    const logs = logger.exportLogs();
    console.log('Logs exported to clipboard');
    navigator.clipboard.writeText(logs);
  };

  (window as any).showCaller = (show: boolean = true) => {
    logger.setShowCaller(show);
    console.log(`Caller info ${show ? 'enabled' : 'disabled'}`);
  };

  (window as any).hideCaller = () => {
    logger.setShowCaller(false);
    console.log('Caller info disabled');
  };

  (window as any).showTimestamp = (show: boolean = true) => {
    logger.setShowTimestamp(show);
    console.log(`Timestamps ${show ? 'enabled' : 'disabled'}`);
  };

  (window as any).showCategory = (show: boolean = true) => {
    logger.setShowCategory(show);
    console.log(`Categories ${show ? 'enabled' : 'disabled'}`);
  };
}